"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Play } from "lucide-react";
import posthog from "posthog-js";
import { Container } from "@/components/shared/container";
import { AnimateOnScroll } from "@/components/shared/animate-on-scroll";
import { DemoButton } from "@/components/demo-button";

const VIDEO_SRC =
  "https://nbbcgpmuwkrzsdyoxqyu.supabase.co/storage/v1/object/public/Videos/Vetina%20-%20homepage-720p.mp4";

const PROGRESS_MILESTONES = [25, 50, 75, 100] as const;
const EVENT_SOURCE = "homepage_video";

export function HomepageVideoStripe() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldMountVideo, setShouldMountVideo] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const reachedRef = useRef<Set<number>>(new Set());

  // Video mount-uje się DOPIERO po kliknięciu play. Plik to 130 MB —
  // auto-mount przy IntersectionObserver pobierałby cały plik niepotrzebnie
  // dla wszystkich, którzy w ogóle scrollują w pobliże sekcji.

  useEffect(() => {
    if (!shouldMountVideo) return;
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      if (!reachedRef.current.has(0)) {
        reachedRef.current.add(0);
        posthog.capture("homepage_video_started", {
          source: EVENT_SOURCE,
          duration_s: Number.isFinite(video.duration) ? Math.round(video.duration) : null,
        });
      }
    };

    const handleTimeUpdate = () => {
      if (!video.duration || !Number.isFinite(video.duration)) return;
      const pct = (video.currentTime / video.duration) * 100;
      for (const milestone of PROGRESS_MILESTONES) {
        if (pct >= milestone && !reachedRef.current.has(milestone)) {
          reachedRef.current.add(milestone);
          posthog.capture("homepage_video_progress", {
            source: EVENT_SOURCE,
            milestone_pct: milestone,
          });
        }
      }
    };

    const handleEnded = () => {
      if (!reachedRef.current.has(-1)) {
        reachedRef.current.add(-1);
        posthog.capture("homepage_video_completed", { source: EVENT_SOURCE });
      }
    };

    const handlePause = () => {
      if (video.ended) return;
      if (!video.duration || !Number.isFinite(video.duration)) return;
      const pct = Math.round((video.currentTime / video.duration) * 100);
      posthog.capture("homepage_video_paused", {
        source: EVENT_SOURCE,
        progress_pct: pct,
      });
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("pause", handlePause);
    };
  }, [shouldMountVideo]);

  function handlePosterClick() {
    setShouldMountVideo(true);
    setHasStarted(true);
  }

  return (
    <section ref={sectionRef} className="py-16 md:py-24">
      <Container className="max-w-4xl">
        <AnimateOnScroll className="text-center max-w-2xl mx-auto mb-8 md:mb-12">
          <p className="text-sm font-medium tracking-wide text-primary uppercase mb-3">
            Vetina w pigułce
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-balance">
            Zobacz, co robimy inaczej
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">
            Krótki film, w którym pokazujemy, co konkretnie odróżnia Vetinę
            od systemów, które dziś dominują na rynku. Bez slajdów,
            bez marketingowego żargonu.
          </p>
        </AnimateOnScroll>

        <AnimateOnScroll delay={0.1}>
          <div className="relative aspect-video overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-blue-500/15 to-primary/10 shadow-2xl shadow-black/20 ring-1 ring-black/5">
            {shouldMountVideo && (
              <video
                ref={videoRef}
                src={VIDEO_SRC}
                controls
                autoPlay
                playsInline
                preload="auto"
                className="h-full w-full"
              />
            )}
            {!hasStarted && (
              <Image
                src="/images/homepage-video-poster.jpg"
                alt="Vetina w pigułce — Mikołaj Nowak, prezes zarządu"
                fill
                sizes="(max-width: 768px) 100vw, 896px"
                className="object-cover"
              />
            )}
            {!hasStarted && (
              <button
                type="button"
                onClick={handlePosterClick}
                aria-label="Odtwórz film"
                className="group absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-black/30 via-black/10 to-black/30 transition-colors hover:from-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/95 shadow-xl transition-transform group-hover:scale-110 md:h-24 md:w-24">
                  <Play className="ml-1 h-8 w-8 text-primary fill-primary md:h-10 md:w-10" />
                </span>
                <span className="text-white/95 text-sm md:text-base font-medium drop-shadow-lg">
                  Vetina w pigułce — odtwórz film
                </span>
              </button>
            )}
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll delay={0.2}>
          <div className="mt-8 md:mt-10 text-center">
            <DemoButton
              size="lg"
              source={EVENT_SOURCE}
              className="text-base px-8 shadow-lg shadow-primary/20"
            >
              Chętnie do Państwa oddzwonimy
            </DemoButton>
            <p className="mt-3 text-sm text-muted-foreground">
              Krótka rozmowa, bez prezentacji handlowej. Oddzwonimy w ciągu 24 godzin.
            </p>
          </div>
        </AnimateOnScroll>
      </Container>
    </section>
  );
}
